import { useNavigate } from "react-router-dom";
import type { AITestDto } from "@mmm/contracts";
import { TestStatusBadge } from "../molecules/TestStatusBadge.js";
import { deleteAITest } from "../../api/commands.js";
import { getAITest } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import { ListRow } from "../molecules/ListRow.js";
import { useConfirm } from "../../hooks/useConfirm.js";
import { IconButton } from "../molecules/IconButton.js";
import { Button, Card, Icon, useT } from "@mmm/ui";
function TestRow({ test, subjectId, onDelete, compact = false }: { test: AITestDto & { subjectTitle?: string }; subjectId?: string; onDelete: () => void; compact?: boolean }) {
  const navigate = useNavigate();
  const t = useT();
  const { data: aggregate } = useQuery(getAITest, { testId: test.id });

  const targetSubjectId = subjectId ?? test.subjectId;
  return (
    <ListRow
      className="mmm-test-row"
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/subjects/${targetSubjectId}/tests/${test.id}`)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate(`/subjects/${targetSubjectId}/tests/${test.id}`);
        }
      }}
      leading={aggregate ? (
        <span className="mmm-test-row-status">
          <TestStatusBadge attempts={aggregate.attempts} />
        </span>
      ) : undefined}
      title={<span className="mmm-test-row-title">{test.title}</span>}
      meta={test.subjectTitle}
      trailing={compact ? undefined : (
        <div className="mmm-item-actions">
          <Button onClick={(event) => {
            event.stopPropagation();
            navigate(`/subjects/${targetSubjectId}/tests/${test.id}`);
          }}>
            <Icon name="doc" />{t("common.open")}
          </Button>
          <IconButton
            icon="trash"
            label={t("tests.deleteTestNamed", { title: test.title })}
            className="mmm-row-action-btn mmm-row-action-btn--danger"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
          />
          </div>
      )}
    />
  );
}

export function TestList({ tests, subjectId, variant = "default" }: { tests: Array<AITestDto & { subjectTitle?: string }>; subjectId?: string; variant?: "default" | "compact" }) {
  const confirm = useConfirm();
  const t = useT();

  const handleDelete = async (test: AITestDto) => {
    const ok = await confirm({
      title: t("tests.deleteTest"),
      message: t("tests.deleteTestConfirm", { title: test.title }),
      confirmLabel: t("tests.deleteTest"),
    });
    if (!ok) return;
    try {
      await deleteAITest({ testId: test.id });
    } catch {
      // Already toasted — the IconButton's onClick invokes this without awaiting it.
    }
  };

  const sorted = tests.slice().sort((a, b) => subjectId
    ? a.sortOrder - b.sortOrder
    : b.createdAt.localeCompare(a.createdAt));

  return (
    <Card className={`mmm-test-list${variant === "compact" ? " mmm-test-list--compact" : ""}`}>
      {sorted.map((test) => (
        <TestRow key={test.id} test={test} subjectId={subjectId} onDelete={() => handleDelete(test)} compact={variant === "compact"} />
      ))}
    </Card>
  );
}
